import { createSlice } from '@reduxjs/toolkit';

// Initialize state from localStorage
const getInitialState = () => {
  try {
    const token = localStorage.getItem('token');
    const loginTime = localStorage.getItem('auth_timestamp');

    // Check if token exists and is not expired
    if (token && loginTime) {
      const now = Date.now();
      const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours

      if (now - parseInt(loginTime) > SESSION_DURATION) {
        // Session expired, clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_timestamp');

        return {
          token: null,
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
        };
      }
    }

    return {
      token: token || null,
      isAuthenticated: !!token,
      user: null, // User data will be fetched via getUserProfile query
      loading: false,
      error: null,
    };
  } catch (error) {
    return {
      token: null,
      isAuthenticated: false,
      user: null,
      loading: false,
      error: null,
    };
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    setCredentials: (state, { payload: { user, token } }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Auth] setCredentials called — token:', token?.substring(0, 20) + '...');
      }
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.error = null;

      // Persist token to localStorage (both keys for compatibility)
      localStorage.setItem('token', token);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_timestamp', Date.now().toString());
      if (user) {
        localStorage.setItem('auth_user', JSON.stringify(user));
        // Also set 'user' (ID) and 'username' keys that useMyClosetState reads
        // Without these, the closet hook thinks there's no session and clears localStorage
        if (user.id) localStorage.setItem('user', user.id);
        if (user.username) localStorage.setItem('username', user.username);
      }
    },
    logout: (state) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Auth] LOGOUT dispatched — clearing token. Stack:', new Error().stack);
      }
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear auth-related localStorage items
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_timestamp');
      localStorage.removeItem('user');
      localStorage.removeItem('username');
    },
    setUser: (state, { payload }) => {
      state.user = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setCredentials,
  logout,
  setUser,
  setLoading,
  setError,
  clearError,
} = authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
